// Utility functions
export * from './chat-transforms';
export * from './case-transform';
export * from './error';
export * from './analysis-helpers';
export * from './objection-handling';
export * from './quality-scale';
export * from './agent-collapse';
export * from './employee-roster';
export * from './agent-ranking';
export * from './period-window';
export * from './deadline';
export * from './bbcode';
export * from './sql-timestamp';
export * from './sentence-end';
export * from './org-language';

export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

export function timestampToSeconds(timestamp: string): number {
  const parts = timestamp.split(':').map(Number);
  if (parts.length === 2 && parts[0] !== undefined && parts[1] !== undefined) {
    return parts[0] * 60 + parts[1];
  }
  if (
    parts.length === 3 &&
    parts[0] !== undefined &&
    parts[1] !== undefined &&
    parts[2] !== undefined
  ) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return 0;
}

/**
 * Parse an alert/transcript timecode in SS, MM:SS or HH:MM:SS form.
 *
 * Unlike the legacy timestampToSeconds helper, this distinguishes a valid
 * zero from malformed input and accepts a seconds-only value.
 */
export function parseTimecode(timecode: string | null | undefined): number | null {
  if (typeof timecode !== 'string') return null;

  const trimmed = timecode.trim();
  if (!/^\d+(?::\d{1,2}){0,2}$/.test(trimmed)) return null;

  const parts = trimmed.split(':').map(Number);
  if (parts.some((part) => !Number.isSafeInteger(part) || part < 0)) return null;
  if (parts.length > 1 && parts.slice(1).some((part) => part >= 60)) return null;

  const seconds = parts.reduce((total, part) => total * 60 + part, 0);
  return Number.isSafeInteger(seconds) ? seconds : null;
}

/**
 * Rounding tolerance for a timecode against the record's duration — the one
 * value for the cards and the printed report. The backend clamp
 * (`apps/workflows-api/src/workers/analyze/safety-gates.ts`) repeats it and a
 * test there compares the two: they must agree on what counts as "beyond the
 * recording".
 */
export const TIMECODE_DURATION_TOLERANCE_SECONDS = 2;

/**
 * Whether a parsed timecode lies beyond the end of the recording — a
 * hallucinated time no player can seek to. False whenever either number is
 * unknown: nothing to compare is not evidence of a bad time.
 */
export function isTimecodeBeyondDuration(
  seconds: number | null,
  durationSeconds: number | null | undefined
): boolean {
  return (
    seconds !== null &&
    durationSeconds != null &&
    seconds > durationSeconds + TIMECODE_DURATION_TOLERANCE_SECONDS
  );
}

/**
 * Safely parse JSON with fallback value
 * Prevents runtime crashes from malformed JSON in database or external sources
 *
 * @param json - JSON string to parse (can be null/undefined)
 * @param fallback - Default value if parsing fails
 * @returns Parsed value or fallback
 *
 * @example
 * const config = safeJsonParse(row.configJson, {});
 * const items = safeJsonParse<string[]>(row.itemsJson, []);
 */
export function safeJsonParse<T>(json: string | null | undefined, fallback: T): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

/**
 * Safely parse JSON with logging on failure
 * Use when you need visibility into parsing failures
 *
 * @param json - JSON string to parse
 * @param fallback - Default value if parsing fails
 * @param context - Context for logging (field name, source)
 * @param logger - Optional logging function
 * @returns Parsed value or fallback
 */
export function safeJsonParseWithLog<T>(
  json: string | null | undefined,
  fallback: T,
  context: { field: string; source?: string },
  logger?: (msg: string, extra: Record<string, unknown>) => void
): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch (error) {
    if (logger) {
      logger(`Failed to parse JSON field: ${context.field}`, {
        source: context.source,
        error: error instanceof Error ? error.message : 'Unknown error',
        jsonPreview: json.slice(0, 100),
      });
    }
    return fallback;
  }
}

// Export project settings utilities
export {
  parseProjectSettings,
  parseAsrSettings,
  parseLlmSettings,
  parseEmailSettings,
  parseCrmSettings,
  getAsrSettings,
  getLlmSettings,
  getEmailSettings,
  getCrmSettings,
  isAutoAnalysisEnabled,
  isScriptAdherenceEnabled,
  type PlatformProviderDefaults,
} from './project-settings';

// Export channel settings utilities
export {
  parseOrgChannelSettings,
  getOrgChannelDefaults,
  resolveChannelSettings,
  isChannelEnabled,
  isChannelEnabledSimple,
  getChannelSettingsResponse,
  type OrgChannelDefaults,
  type OrgChannelSettings,
} from './channel-settings';

// Export contact masking (personal notification channels — audit 2026-08-11 v3)
export {
  maskContactName,
  maskPhoneTail,
  PHONE_MASK_VISIBLE_DIGITS,
} from './contact-masking';

// Export retention settings utilities
export {
  resolveRetentionDays,
  resolveRetentionFromOrgSettingsJson,
  retentionHorizonAt,
  getRetentionCutoffDate,
  shouldDeleteRecord,
  isValidRetentionDays,
  isValidCustomerRetentionDays,
  PLATFORM_RETENTION_DAYS,
  MAX_RETENTION_DAYS,
  MIN_RETENTION_DAYS,
  MIN_CUSTOMER_RETENTION_DAYS,
  MAX_CUSTOMER_RETENTION_DAYS,
  MAX_CALL_INDEX_PREFILL_SPAN_DAYS,
  MAX_CALL_INDEX_PREFILL_SPAN_MS,
  type PlatformRetentionDefaults,
  type OrgRetentionSettings,
  type ResolvedRetentionSettings,
} from './retention-settings';

// Character offset in the stored transcript → second of the call (#758)
export {
  transcriptCharOffsets,
  transcriptOffsetToSeconds,
  type TranscriptOffsetSegment,
} from './transcript-offset';

// Export per-billing-cycle notification dedupe (budget thresholds, minute events)
export {
  shouldEmitForCycle,
  NOTIFICATION_DEDUPE_LOOKBACK_DAYS,
} from './notification-dedupe';
